import EmptyState from '@pinecast/common/EmptyState';
import ErrorState from '@pinecast/common/ErrorState';
import Group from '@pinecast/common/Group';
import {RightArrowSmall} from '@pinecast/common/icons';
import Link from '@pinecast/common/Link';
import LoadingState from '@pinecast/common/LoadingState';
import Section from '@pinecast/common/Section';
import {usePageTitleEffect} from '@pinecast/db-spa/App/usePageTitle';
import {useQuery} from '@pinecast/gql';
import type {
  DonationPage as DonationPageQuery,
  DonationPage_podcast,
  DonationPage_tipJarSubscription,
  DonationPageVariables,
} from '@pinecast/gql/__generated_tj__/DonationPage';
import {NextRecurringTipStateEnum} from '@pinecast/gql/__generated_tj__/globalTypes';
import {defineMessage, FormattedMessage} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import gql from 'graphql-tag';
import * as React from 'react';
import {useParams} from 'react-router';

import DonationPageHeader from './DonationPageHeader';
import TipAmountSelection from './TipAmountSelection';
import TipFrequency from './TipFrequency';
import TipJarDone from './TipJarDone';
import TipJarPendingVerification from './TipJarPendingVerification';
import TipJarRequestLogin from './TipJarRequestLogin';
import {useGetSubscriptionCheckoutSession} from './useGetSubscriptionCheckoutSession';
import {stripeKey, useStripe} from './useStripe';

const messages = {
  title: defineMessage({
    description:
      'Title for podcast donation page. {podcast} is the name of the podcast',
    defaultMessage: 'Subscribe to {podcast}',
  }),
};

const DONATION_PAGE = gql`
  query DonationPage($slug: ID!) {
    podcast(slug: $slug) {
      slug
      name
      canHaveRecurringTips
      coverArtThumbnail(size: 128)
      hasPrivateEpisodes
      privateAccessMinSubscription
      tipLimit
      tipJarActive
      tipJarAccountId
      tipJarCurrency
      tipJarMessage
      tipJarSuccessMessage
    }
    tipJarSubscription(podcastSlug: $slug) {
      id
      amount
      currency
      willCancel
    }
  }
`;

export type Step =
  | 'initial'
  | 'frequency'
  | 'require_login'
  | 'done'
  | 'pending_verification';

const DonationPage = () => {
  const params = useParams<{slug: string}>();

  // React Router v6 useParams can return undefined values
  if (!params.slug) {
    throw new Error('Missing required route parameter: slug');
  }
  const css = useCSS();
  const query = useQuery<DonationPageQuery, DonationPageVariables>(
    DONATION_PAGE,
    {variables: {slug: params.slug}},
  );

  usePageTitleEffect(() => {
    if (!query.data?.podcast) {
      return;
    }
    return [messages.title, {podcast: query.data.podcast.name}];
  }, [query]);

  return (
    <div
      className={css({
        margin: '20px auto',
        maxWidth: '720px',
        padding: '0 12px',
      })}
    >
      <DonationPageHeader
        slug={params.slug}
        podcast={query.data?.podcast ?? null}
      />
      {query.loading ? (
        <Section>
          <LoadingState
            title={
              <FormattedMessage
                description="Message for when the donation page content is loading"
                defaultMessage="Loading podcast information…"
              />
            }
          />
        </Section>
      ) : query.error ? (
        <Section>
          <ErrorState
            title={
              <FormattedMessage
                description="Message for when the donation page content has errored"
                defaultMessage="Unable to load podcast"
              />
            }
          />
        </Section>
      ) : !query.data.podcast || !query.data.podcast.tipJarActive ? (
        <Section>
          <ErrorState
            title={
              <FormattedMessage
                description="Message for when a tip jar is inactive"
                defaultMessage="This podcast's tip jar is not available."
              />
            }
            copy={
              <FormattedMessage
                description="Copy for when a tip jar is inactive"
                defaultMessage="Contact the show's owner for support."
              />
            }
          />
        </Section>
      ) : (
        <>
          {query.data.tipJarSubscription && (
            <Link
              href={`/subscriptions/recurring_tips/${query.data.tipJarSubscription.id}`}
              style={{
                display: 'block',
                marginBottom: '16px',
                marginTop: '-12px',
              }}
            >
              <Group spacing={4} centerChildren>
                <FormattedMessage
                  description="Link to manage existing tip jar subscription"
                  defaultMessage="Manage your existing subscription"
                />
                <RightArrowSmall />
              </Group>
            </Link>
          )}
          {query.data.podcast.tipJarMessage && (
            <Section>{query.data.podcast.tipJarMessage}</Section>
          )}
          {query.data.tipJarSubscription?.willCancel ? (
            <Section>
              <EmptyState
                title={
                  <FormattedMessage
                    description="Title when there is an existing subscription that will cancel"
                    defaultMessage="You have an existing subscription set to cancel"
                  />
                }
                copy={
                  <FormattedMessage
                    description="Copy when there is an existing subscription that will cancel"
                    defaultMessage="You've previously set up a subscription to this podcast that's set to cancel automatically at the end of the current billing period. If you'd like to change this subscription, make it recurring first."
                  />
                }
                actionLabel={
                  <FormattedMessage
                    description="Button when there is an existing subscription that will cancel"
                    defaultMessage="Update subscription"
                  />
                }
                onAction={`/subscriptions/recurring_tips/${query.data.tipJarSubscription.id}`}
              />
            </Section>
          ) : (
            <DonationPageInner
              podcast={query.data.podcast}
              subscription={query.data.tipJarSubscription}
            />
          )}
        </>
      )}
    </div>
  );
};

const DonationPageInner = ({
  podcast,
  subscription,
}: {
  podcast: DonationPage_podcast;
  subscription: DonationPage_tipJarSubscription | null;
}) => {
  const stripe = useStripe(stripeKey, podcast.tipJarAccountId!);

  const [step, setStep] = React.useState<Step>('initial');
  const [amount, setAmount] = React.useState<number | null>(
    subscription?.currency === podcast.tipJarCurrency
      ? subscription?.amount
      : null,
  );
  const [oneTime, setOneTime] = React.useState(false);

  const getSubscriptionCheckoutSession = useGetSubscriptionCheckoutSession();

  async function handleGetSession(
    slug: string,
    frequency: 'oneTime' | 'recurring',
    amount: number,
  ) {
    const result = await getSubscriptionCheckoutSession.action({
      variables: {
        podcastSlug: slug,
        amount,
        oneTime: frequency === 'oneTime',
      },
    });

    switch (result!.createSubscriptionCheckoutSession!.nextState) {
      case NextRecurringTipStateEnum.READY:
        await (await stripe)!.redirectToCheckout({
          sessionId: result.createSubscriptionCheckoutSession!.sessionId!,
        });
        return 'noop';
      default:
        return result.createSubscriptionCheckoutSession!.nextState.toLowerCase();
    }
  }

  return (
    <Section>
      {(step === 'initial' || step == 'frequency') && (
        <TipAmountSelection
          podcast={podcast}
          subscription={subscription}
          onChange={async amount => {
            setAmount(amount);
            if (subscription) {
              if (subscription.amount !== amount) {
                const result = await handleGetSession(
                  podcast.slug,
                  'recurring',
                  amount,
                );
                if (result !== 'noop') {
                  setStep(result as Step);
                }
              }
            } else {
              setStep('frequency');
            }
          }}
          value={amount}
        />
      )}
      {step === 'frequency' && (
        <TipFrequency
          onChange={async frequency => {
            setOneTime(frequency === 'oneTime');
            const result = await handleGetSession(
              podcast.slug,
              frequency,
              amount!,
            );
            if (result !== 'noop') {
              setStep(result as Step);
            }
          }}
        />
      )}
      {step === 'require_login' && (
        <TipJarRequestLogin
          slug={podcast.slug}
          amount={amount!}
          oneTime={oneTime}
          onContinue={setStep}
        />
      )}
      {step === 'pending_verification' && <TipJarPendingVerification />}
      {step === 'done' && <TipJarDone />}
    </Section>
  );
};

export default DonationPage;
